import { Contract } from "@/services/backend/vbs/contracts/types/contract";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { PricingCards } from "@/services/backend/vbs/pricing/components/pricing-cards";
import { CalculationComponent } from "@/routes/processes/[processId]/vbs/calculations/[calculationId]/calculation/calculation-route";
import { TreatmentComponent } from "@/services/backend/vbs/treatments/components/treatment-component";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { RemoveTreatmentCycleDialog } from "@/routes/processes/[processId]/vbs/offers/[offerId]/contract/_components/remove-treatment-cycle-dialog";
import { AddTreatmentCycleDialog } from "@/routes/processes/[processId]/vbs/offers/[offerId]/contract/_components/add-treatment-cycle-dialog";
import { CopyFromOfferDialog } from "@/routes/processes/[processId]/vbs/offers/[offerId]/contract/_components/copy-from-offer-dialog";
import { PricingByAreaCard } from "@/services/backend/vbs/pricing/components/pricing-by-area-card";

export function TreatmentCycles({
  contract,
  offerId,
}: {
  contract: Contract;
  offerId: string;
}) {
  const [cycle, setCycle] = useState(
    contract.treatmentCycles.length > 0 ? contract.treatmentCycles[0].cycle : 0,
  );
  const cycles = useMemo(
    () =>
      contract.treatmentCycles.map((tc) => tc.cycle).toSorted((a, b) => a - b),
    [contract.treatmentCycles],
  );
  const disabled = contract.immutable;

  useEffect(() => {
    if (!cycles.includes(cycle) && cycles.length > 0) {
      setCycle(cycles[0]);
    }
  }, [cycle, cycles]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Behandlung")}</CardTitle>
        <CardDescription>
          {t("Die Behandlung ist bei Verträgen nach Turnus aufgeteilt.")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="pricing">
          <div className="flex gap-2">
            <CycleSelect
              cycle={cycle}
              onCycleChange={setCycle}
              cycles={cycles}
            />
            {!disabled && (
              <>
                <RemoveTreatmentCycleDialog contract={contract} cycle={cycle} />
                <AddTreatmentCycleDialog contract={contract} />
              </>
            )}
            <TabsList>
              <TabsTrigger value="pricing">{t("Preise")}</TabsTrigger>
              <TabsTrigger value="calculation">{t("Kalkulation")}</TabsTrigger>
              <TabsTrigger value="treatment">{t("Leistungen")}</TabsTrigger>
            </TabsList>
            <div className="ml-auto flex gap-2">
              <PersistenceStateUsingContext />
              {!disabled && (
                <CopyFromOfferDialog contract={contract} offerId={offerId} />
              )}
            </div>
          </div>
          {contract.treatmentCycles
            .filter((tc) => tc.cycle === cycle)
            .map((tc) => (
              <Fragment key={tc.treatmentId}>
                <TabsContent value="pricing">
                  <div className="grid gap-2">
                    <PricingCards
                      pricingId={tc.pricingId}
                      disabled={disabled}
                    />
                    <PricingByAreaCard
                      pricingByAreaId={tc.pricingByAreaId}
                      disabled={disabled}
                    />
                  </div>
                </TabsContent>
                <TabsContent value="calculation">
                  <CalculationComponent
                    calculationId={tc.calculationId}
                    disabled={disabled}
                  />
                </TabsContent>
                <TabsContent value="treatment">
                  <TreatmentComponent
                    treatmentId={tc.treatmentId}
                    disabled={disabled}
                  />
                </TabsContent>
              </Fragment>
            ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}

function CycleSelect({
  cycle,
  onCycleChange,
  cycles,
}: {
  cycle: number;
  onCycleChange: (cycle: number) => void;
  cycles: number[];
}) {
  return (
    <Select
      value={cycle.toString()}
      onValueChange={(value) => onCycleChange(Number(value))}
    >
      <SelectTrigger>
        <SelectValue placeholder={t("Turnus")} />
      </SelectTrigger>
      <SelectContent>
        {cycles.map((c) => (
          <SelectItem key={c} value={c.toString()}>
            {c} {t("Wochen")}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
